/// <amd-module name="Build/Common/Commands/queuebuild/Command" />
import * as React from "react";

import { Build, BuildDefinitionVariable } from "Build/Client/Contracts/Build";
import { IQueueBuildProps } from "Build/Common/Commands/queuebuild/Command.types";
import { QueuePanel } from "Build/Queue-Panel/QueuePanel";
import { QueueStore } from "Build/Common/Library/Legacy/Stores/Queue";
import { getSingletonManager } from "Build/Common/Library/SingletonManager";

import { TaskAgentQueue } from "DistributedTask/Client/Contracts/TaskAgent";

import { VssComponent } from "VSS/Platform/Layout";

import {
    ICIQueueBuildProps,
    IQueueBuildState,
    VariableList
} from "./Types";

class QueueBuild extends VssComponent<IQueueBuildProps, IQueueBuildState> {
    public static componentType = "ciQueueBuildComponent";

    private _queueStore: QueueStore;

    constructor(props: IQueueBuildProps) {
        super(props);
        props = props as IQueueBuildProps;
        let currentStore = getSingletonManager().get<QueueStore>(QueueStore.key);
        if (!currentStore) {
            currentStore = new QueueStore({
                pageContext: props.pageContext,
                project: props.project
            });
            getSingletonManager().add<QueueStore>(QueueStore.key, currentStore);
        }

        this._queueStore = currentStore;

        this.state = {
            queues: this._queueStore.getQueues()
        };
    }

    public componentDidMount() {
        this._queueStore.addListener(this._storeChanged);
    }

    public componentWillUnmount() {
        this._queueStore.removeListener(this._storeChanged);
    }

    public render() {
        const definition = this.props.definition;
        let queueBuildOptions: ICIQueueBuildProps = {
            agentQueues: this.state.queues,
            defaultAgentQueue: null,
            defaultSourceBranch: this.props.defaultSourceBranch || "",
            definitionId: definition.id,
            definitionName: definition.name,
            processType: this.props.processType as number,
            repository: this.props.repository,
            queueDialogSource: this.props.source,
            onSuccess: this._onSuccess,
            onClosed: this.props.onClosed,
            definitionQueueStatus: definition.queueStatus,
            runTimeVariables: this._getRuntimeVariables(this.props.definitionVariables, this.props.previousRunVariables),
            previousTemplateParameters: this.props.previousTemplateParameters,
            addDefinitionDemands: true
        };

        const queue = this.props.queue;
        if (queue) {
            queueBuildOptions.defaultAgentQueue = { projectId: this.props.project, id: queue.id, name: queue.name, pool: queue.pool } as TaskAgentQueue;
        }

        return <QueuePanel
            showPanel={true}
            pageContext={this.props.pageContext}
            projectId={this.props.project}
            definitionId={this.props.definition.id}
            buildRepository={this.props.repository}
            sourceBranch={this.props.defaultSourceBranch || ""}
            runtimeVariables={queueBuildOptions.runTimeVariables}
            previousTemplateParameters={queueBuildOptions.previousTemplateParameters}
            definitionVariables={this.props.definitionVariables}
            onDismiss={this.props.onDismiss} />
    }

    private _storeChanged = () => {
        this.setState({
            queues: this._queueStore.getQueues()
        });
    }

    private _onSuccess = (link: string, buildNumber: string, build: Build) => {
        this.props.onSuccess(build);
    }

    private _getRuntimeVariables(definitionVariables: { [key: string]: BuildDefinitionVariable } | undefined, previousRunVariables: { [key: string]: string } | undefined): VariableList {
        const runtimeVariables: VariableList = [];
        definitionVariables = definitionVariables || {};
        previousRunVariables = previousRunVariables || {};

        for (let variableName in definitionVariables) {
            // if a definition variable was overwritten in previous run, we'll keep the new value
            if (definitionVariables.hasOwnProperty(variableName) && !previousRunVariables.hasOwnProperty(variableName)) {
                let variable = definitionVariables[variableName];
                if (variable.allowOverride) {
                    runtimeVariables.push({
                        name: variableName,
                        variable: {
                            value: variable.value,
                            isSecret: variable.isSecret,
                            allowOverride: true,
                            disableDelete: true
                        }
                    });
                }
            }
        }

        // preserve runtime variables from a previous run if queuing from run results page
        for (let variableName in previousRunVariables) {
            if (previousRunVariables.hasOwnProperty(variableName)) {
                let variableValue = previousRunVariables[variableName];
                let isOverwrittenDefinitionVariable = definitionVariables.hasOwnProperty(variableName) && definitionVariables[variableName].allowOverride === true;
                runtimeVariables.push({
                    name: variableName,
                    variable: {
                        value: variableValue,
                        isSecret: false,
                        allowOverride: true,
                        disableDelete: isOverwrittenDefinitionVariable
                    }
                });
            }
        }

        return runtimeVariables;
    }
}

VssComponent.register(QueueBuild.componentType, QueueBuild);
